<template>
  <div class="metadata-editor">
    <label class="label">Metadata</label>

    <p v-if="!fields.length" class="help">No metadata yet — add a field below.</p>

    <div v-for="(field, idx) in fields" :key="field.uid" class="box metadata-field">
      <div class="metadata-field-head">
        <input class="input is-small metadata-key" v-model="field.key" @input="emit"
               placeholder="field name" />
        <div class="select is-small">
          <select v-model="field.widget" @change="onWidgetChange(field)">
            <option value="text">Text</option>
            <option value="textarea">Long text</option>
            <option value="list">List</option>
            <option value="image">Image</option>
            <option value="object">Group</option>
          </select>
        </div>
        <button class="button is-small is-danger is-light" @click="removeField(idx)" title="remove field">✕</button>
      </div>

      <div class="metadata-field-body">
        <!-- long text -->
        <textarea v-if="field.widget === 'textarea'" class="textarea is-small" rows="3"
                  v-model="field.value" @input="emit"></textarea>

        <!-- list of values (e.g. tags) -->
        <div v-else-if="field.widget === 'list'" class="tags-editor">
          <span v-for="(item, i) in field.value" :key="i" class="tag is-info is-medium">
            {{ item }}
            <button class="delete is-small" @click="removeItem(field, i)"></button>
          </span>
          <input class="input is-small tag-add" v-model="field.draft"
                 @keyup.enter.prevent="addItem(field)" placeholder="type + Enter" />
        </div>

        <!-- nested group (object) -->
        <div v-else-if="field.widget === 'object'" class="nested">
          <div v-for="(sub, k) in field.value" :key="k" class="field is-horizontal nested-row">
            <label class="label is-small nested-label">{{ humanize(k) }}</label>
            <input class="input is-small" :value="sub" @input="setSub(field, k, $event.target.value)" />
            <button class="button is-small is-danger is-light" @click="removeSub(field, k)">✕</button>
          </div>
          <div class="nested-add">
            <input class="input is-small" v-model="field.subKey" placeholder="property" />
            <button class="button is-small is-link is-light" @click="addSub(field)">Add property</button>
          </div>
        </div>

        <!-- image: filename + best-effort preview -->
        <div v-else-if="field.widget === 'image'" class="image-field">
          <input class="input is-small" v-model="field.value" @input="emit" placeholder="image filename" />
          <img v-if="field.value" class="metadata-thumb" :src="imageUrl(field.value)"
               @error="$event.target.style.display='none'"
               @load="$event.target.style.display='block'" alt="" />
        </div>

        <!-- default single-line text -->
        <input v-else class="input is-small" v-model="field.value" @input="emit" />
      </div>
    </div>

    <div class="add-field">
      <input class="input is-small" v-model="newKey" placeholder="new field name"
             @keyup.enter.prevent="addField" />
      <div class="select is-small">
        <select v-model="newType">
          <option value="text">Text</option>
          <option value="textarea">Long text</option>
          <option value="list">List</option>
          <option value="image">Image</option>
          <option value="object">Group</option>
        </select>
      </div>
      <button class="button is-small is-link" @click="addField">Add field</button>
    </div>
  </div>
</template>

<script>
import env from "../../../env/env"
import { tenantStore } from "../../tenant/tenant"

let seq = 0

// Infer a friendly widget from the data itself — no per-tenant field list.
function inferWidget(key, value) {
  if (Array.isArray(value)) return 'list'
  if (value !== null && typeof value === 'object') return 'object'
  const k = String(key).toLowerCase()
  if (/(image|photo|img|logo|avatar|picture)/.test(k)) return 'image'
  if (/(bio|biography|description|about|reason|summary|text|body)/.test(k)) return 'textarea'
  if (typeof value === 'string' && value.length > 60) return 'textarea'
  return 'text'
}

function emptyFor(widget) {
  if (widget === 'list') return []
  if (widget === 'object') return {}
  return ''
}

export default {
  name: 'MetadataEditor',
  props: {
    modelValue: { type: Object, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      fields: this.toFields(this.modelValue),
      newKey: '',
      newType: 'text',
    }
  },
  methods: {
    toFields(obj) {
      const o = (obj && typeof obj === 'object') ? obj : {}
      return Object.keys(o).map(key => ({
        uid: ++seq,
        key,
        widget: inferWidget(key, o[key]),
        value: Array.isArray(o[key]) ? [...o[key]]
             : (o[key] && typeof o[key] === 'object') ? { ...o[key] } : o[key],
        draft: '',
        subKey: '',
      }))
    },
    build() {
      const out = {}
      this.fields.forEach(f => {
        const key = (f.key || '').trim()
        if (key) { out[key] = f.value }
      })
      return out
    },
    emit() {
      this.$emit('update:modelValue', this.build())
    },
    humanize(k) {
      return String(k).replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    imageUrl(file) {
      const code = tenantStore.selected
      return `${env.MEDIA_URL}/media/${code}/${file}`
    },
    onWidgetChange(field) {
      const empty = emptyFor(field.widget)
      const cur = field.value
      // keep compatible values; otherwise reset to the widget's empty shape
      if (field.widget === 'list' && !Array.isArray(cur)) field.value = cur ? [String(cur)] : []
      else if (field.widget === 'object' && (cur === null || typeof cur !== 'object' || Array.isArray(cur))) field.value = {}
      else if ((field.widget === 'text' || field.widget === 'textarea' || field.widget === 'image')
               && (Array.isArray(cur) || (cur && typeof cur === 'object'))) field.value = ''
      if (field.value === undefined) field.value = empty
      this.emit()
    },
    addItem(field) {
      const v = (field.draft || '').trim()
      if (v) { field.value.push(v); field.draft = ''; this.emit() }
    },
    removeItem(field, i) { field.value.splice(i, 1); this.emit() },
    setSub(field, k, v) { field.value[k] = v; this.emit() },
    addSub(field) {
      const k = (field.subKey || '').trim()
      if (k) { field.value[k] = ''; field.subKey = ''; this.emit() }
    },
    removeSub(field, k) { delete field.value[k]; this.emit() },
    addField() {
      const key = (this.newKey || '').trim()
      if (!key) return
      this.fields.push({ uid: ++seq, key, widget: this.newType, value: emptyFor(this.newType), draft: '', subKey: '' })
      this.newKey = ''
      this.newType = 'text'
      this.emit()
    },
    removeField(idx) { this.fields.splice(idx, 1); this.emit() },
  },
}
</script>

<style scoped>
.metadata-editor { margin-top: 0.5rem; }
.metadata-field { padding: 0.6rem 0.75rem; margin-bottom: 0.6rem; }
.metadata-field-head { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.4rem; }
.metadata-key { font-weight: 600; max-width: 12rem; }
.tags-editor { display: flex; flex-wrap: wrap; gap: 0.35rem; align-items: center; }
.tag-add { max-width: 10rem; }
.nested-row { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.35rem; }
.nested-label { min-width: 6rem; margin-bottom: 0; text-align: right; }
.nested-add { display: flex; gap: 0.4rem; margin-top: 0.3rem; }
.image-field { display: flex; gap: 0.6rem; align-items: flex-start; }
.metadata-thumb { width: 56px; height: 56px; object-fit: cover; border-radius: 4px; border: 1px solid #dbdbdb; }
.add-field { display: flex; gap: 0.4rem; align-items: center; margin-top: 0.4rem; }
</style>
